import Loader from "../../../../../components/common/Loader";
import WalletOTP from "../../SendFunds/components/WalletOTP";
import * as S from "../styles";
import { formatCurrencyAmount } from "../../../../../utils/currency";
import { v4 as uuidv4 } from "uuid";
import { Button } from "../../../../../components/common/Button";
import React, { useContext, useEffect, useState } from "react";
import { ItemWrapper } from "./styles";
import { clearUserData, setStep } from "../../../redux/formDataSlice";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { payoutRequestV3, setLoading } from "../../../redux/slice";
import { sendOTP } from "../../../../../api/usersAPI";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import { encryptAlgorithm } from "../../../../../utils/encryptionAlgorithm";
import { SuccessAlertContext } from "../../../../../context/SuccessAlertContext";
import { merchantBusinessKeys } from "../../../../Settings/settingsSlice";
import { removeLastCharacter } from "../../../utils";
import { capitalizeCurrencyCode } from "../../../../../utils/helpers";

const Review = ({ handlePrevious }) => {
  const dispatch = useAppDispatch();
  const { currencyCode } = useParams();
  const [verifyOTP, setVerifyOTP] = useState(false);
  const [taxRefId, setTaxRefId] = useState("");
  const { keys } = useAppSelector((state) => state.settings);
  const { loading, wallets } = useAppSelector((state) => state.wallets || {});

  const CURRENT_WALLET = wallets.find((wallet) => wallet.currency === "USD");

  const { businessId } = useAppSelector((state) => state.users);
  const navigate = useNavigate();

  const publicKey = keys?.data?.publicKey;

  const accountUser = useAppSelector(
    (state) => state.users.userObj?.accountUser,
  );
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};
  const { onShowAlert: onShowSuccessAlert } =
    useContext(SuccessAlertContext) || {};

  const getSavedData = useAppSelector((state) => state.formData.intPayoutData);

  const currentDate = dayjs().format("YYYY-MM-DD");

  const handlePrevStep = (index: number) => {
    dispatch(setStep(index));
  };

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId]);

  useEffect(() => {
    let txRefId = uuidv4();
    txRefId = `tranf-${txRefId}`;
    setTaxRefId(txRefId);
  }, []);

  const handlePayout = async () => {
    const payoutRequest = {
      country: removeLastCharacter(capitalizeCurrencyCode(currencyCode)),
      amount: getSavedData?.amount,
      accountName: getSavedData?.beneficiaryName,
      serviceCode: getSavedData?.secondaryPaymentMethod,
      requestId: taxRefId,
      description: getSavedData?.description,
      currency: capitalizeCurrencyCode(currencyCode),
      accountNumber: getSavedData?.beneficiaryPhoneNumber,
      type: "mobile_money",
      debitAmount: getSavedData?.totalAmount,
      walletId: CURRENT_WALLET?.id,
      payoutType: "USD_MOMO",
    };

    const encryptedData = encryptAlgorithm(
      payoutRequest,
      keys?.data?.encryptionKey,
    );

    const payload = JSON.stringify({
      message: encryptedData,
    });

    const action = await dispatch(
      payoutRequestV3({ data: payload, token: publicKey }),
    );

    if (payoutRequestV3.fulfilled.match(action)) {
      onShowSuccessAlert("Payout successful");
      dispatch(clearUserData());
      navigate("/wallets");
    } else if (payoutRequestV3.rejected.match(action)) {
      onShowErrorAlert("", action.payload as string);
    }
  };

  const handleSendOTP = async (user: { email: string }) => {
    try {
      dispatch(setLoading(true));
      await sendOTP(user);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert("", error?.response as string);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <S.ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP setVerifyOTP={setVerifyOTP} makePayment={handlePayout} />
      )}

      <S.DetailsBox>
        <S.DetailsSectionHeader>
          <S.SectionTitle>Transaction details</S.SectionTitle>
          <S.EditButton onClick={() => handlePrevStep(0)}>
            <span>Edit</span>
          </S.EditButton>
        </S.DetailsSectionHeader>

        <S.DetailsContent>
          <S.DetailsRow>
            <S.DetailsLabel>Amount</S.DetailsLabel>
            <S.DetailsValue>
              {formatCurrencyAmount(capitalizeCurrencyCode(currencyCode), getSavedData?.amount)}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Processing fee</S.DetailsLabel>
            <S.DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.fee)}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Rate</S.DetailsLabel>
            <S.DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.rate ?? 0, 5) ?? 0}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Total amount</S.DetailsLabel>
            <S.DetailsValue>
              {formatCurrencyAmount("USD", getSavedData?.totalAmount)}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Payment description</S.DetailsLabel>
            <S.DetailsValue>
              {getSavedData?.description?.substring(0, 20) || "N/A"}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Date</S.DetailsLabel>
            <S.DetailsValue>{currentDate}</S.DetailsValue>
          </S.DetailsRow>
        </S.DetailsContent>

        <S.DetailsSectionHeader>
          <S.SectionTitle>Beneficiary’s details</S.SectionTitle>
          <S.EditButton onClick={() => handlePrevStep(1)}>
            <span>Edit</span>
          </S.EditButton>
        </S.DetailsSectionHeader>
        <S.DetailsContent>
          <S.DetailsRow>
            <S.DetailsLabel>Beneficiary name</S.DetailsLabel>
            <S.DetailsValue>{getSavedData?.beneficiaryName}</S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Momo number</S.DetailsLabel>
            <S.DetailsValue>
              {getSavedData?.beneficiaryPhoneNumber}
            </S.DetailsValue>
          </S.DetailsRow>

          <S.DetailsRow>
            <S.DetailsLabel>Mobile network operator</S.DetailsLabel>
            <S.DetailsValue>
              {getSavedData?.secondaryPaymentMethod
                ?.replace(/_/g, " ")
                .toLowerCase()
                .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase())}
            </S.DetailsValue>
          </S.DetailsRow>
        </S.DetailsContent>
      </S.DetailsBox>

      <ItemWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={() => handleSendOTP(accountUser?.email)}
          label="Continue"
          height="48px"
        />
      </ItemWrapper>
    </S.ContentWrapper>
  );
};

export default Review;
